import { AppModal, Button, Input, MultiSelect } from "@/components/ui";
import { useUserDirectory } from "@/hooks/use-user-directory";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import { formatUsersName } from "@/lib/utils/format";
import { useQuery } from "@tanstack/react-query";
import type { Dispatch, SetStateAction } from "react";

type Props = {
  tabId?: string;
  title: string;
  originalTitle: string;
  setTitle: Dispatch<SetStateAction<string>>;
  teamIds: string[];
  userIds: string[];
  originalTeamIds: string[];
  originalUserIds: string[];
  setTeamIds: Dispatch<SetStateAction<string[]>>;
  setUserIds: Dispatch<SetStateAction<string[]>>;
  error: string;
  setError: Dispatch<SetStateAction<string>>;
  saving: boolean;
  saveDisabled?: boolean;
  onClose: () => void;
  onSave: () => void;
  onDelete?: () => void;
};

export function DashboardTabEditorModal({
  tabId,
  title,
  originalTitle,
  setTitle,
  teamIds,
  userIds,
  originalTeamIds,
  originalUserIds,
  setTeamIds,
  setUserIds,
  error,
  setError,
  saving,
  saveDisabled = false,
  onClose,
  onSave,
  onDelete,
}: Props) {
  const teams = useQuery(retrieveMatterSettingsQueryOptions());
  const users = useUserDirectory({ selectedIds: userIds });
  const userOptions = users.users.map((user) => ({
    label: formatUsersName(user) ?? user.id,
    value: user.id,
  }));
  for (const id of userIds) {
    if (!userOptions.some((option) => option.value === id)) {
      userOptions.push({
        label: `Unavailable user (${id.slice(-6)})`,
        value: id,
      });
    }
  }
  const changed = (a: string[], b: string[]) =>
    [...a].sort().join("\0") !== [...b].sort().join("\0");

  return (
    <AppModal
      open
      onOpenChange={(open) => {
        if (!open && !saving) onClose();
      }}
      title={tabId ? "Edit tab" : "Add tab"}
      size="md"
      isDirty={
        title !== originalTitle ||
        changed(teamIds, originalTeamIds) ||
        changed(userIds, originalUserIds)
      }
      footer={
        <div className="flex w-full items-center justify-between gap-2">
          {tabId && onDelete ? (
            <Button
              type="button"
              variant="destructive"
              onClick={onDelete}
              disabled={saving}
            >
              Delete
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={onSave} disabled={saving || saveDisabled}>
              Done
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-5">
        <div className="space-y-2">
          <label
            htmlFor="dashboard-tab-title"
            className="block text-sm font-medium"
          >
            Tab name <span className="text-destructive">*</span>
          </label>
          <Input
            id="dashboard-tab-title"
            value={title}
            maxLength={200}
            onChange={(event) => {
              setTitle(event.target.value);
              setError("");
            }}
          />
        </div>
        <div className="space-y-2">
          <label className="block text-sm font-medium">Teams</label>
          <MultiSelect
            type="button"
            aria-label="Teams with this tab"
            options={(teams.data?.data.teams ?? []).map((team) => ({
              label: team.label,
              value: team.id,
            }))}
            selectedValues={teamIds}
            setSelectedValues={setTeamIds}
            onValueChange={() => {
              setError("");
            }}
            placeholder="Select teams"
            showToggleAll={false}
            showFirstSelectionAndCount
            modalPopover
            popoverContentClassName="w-[var(--radix-popover-trigger-width)]"
            isLoading={teams.isPending}
          />
        </div>
        <div className="space-y-2">
          <label className="block text-sm font-medium">Users</label>
          <MultiSelect
            type="button"
            aria-label="Users with this tab"
            options={userOptions}
            {...users.selectProps}
            selectedValues={userIds}
            setSelectedValues={setUserIds}
            onValueChange={() => {
              setError("");
            }}
            placeholder="Select users"
            showToggleAll={false}
            showFirstSelectionAndCount
            modalPopover
            popoverContentClassName="w-[var(--radix-popover-trigger-width)]"
            popoverSide="top"
            isLoading={users.isLoading}
          />
        </div>
      </div>
      {error && (
        <p className="text-destructive mt-2 text-sm" role="alert">
          {error}
        </p>
      )}
    </AppModal>
  );
}
